import React,{
  useCallback,
} from 'react';
import clsx from 'clsx';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import isNil from 'lodash/isNil';
import {
  useNotify,
} from 'core/hooks';
import ToolTip from 'core/components/Tooltip';
import Spinner from 'pages/Dashboard/components/Spinner';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  useFetchTelehealthJoinInfo,
  useGetTelehealthJoinInfo,
  useRetryMeetingCreationForAppointment,
} from 'pages/Dashboard/pages/Appointments/pages/List/hooks';
import {
  EncounterViewModel,
} from 'dtos/encounterViewModel';
import {
  hasTelehealthMeeting,
} from 'utils/telehealth';

type Props = {
  encounter: EncounterViewModel;
  className?: string;
};

const joinButtonClassName = 'border rounded !border-blue-500 font-semibold !text-xs py-2 pl-2 pr-2.5 gap-1 flex items-center text-blue-500 !w-fit disabled:opacity-50 disabled:cursor-not-allowed';
const retryButtonClassName = 'border rounded !border-amber-500 font-semibold !text-xs py-2 pl-2 pr-2.5 gap-1 flex items-center text-amber-600 !w-fit disabled:opacity-50 disabled:cursor-not-allowed';

const assignedProviderJoinTooltip = 'Only the assigned provider can join this telehealth visit.';
const telehealthMeetingUnavailableMessage = 'Telehealth meeting is not available for this appointment.';

function getJoinDisabledTooltip(
  doesAppointmentBelongToCurrentUser: boolean,
  canJoinMeeting: boolean,
  joinUnavailableMessage?: string,
): string | undefined {
  if (!doesAppointmentBelongToCurrentUser) {
    return assignedProviderJoinTooltip;
  }

  if (!canJoinMeeting) {
    return joinUnavailableMessage;
  }

  return undefined;
}

type TelehealthActionButtonProps = {
  className?: string;
  buttonClassName: string;
  disabled: boolean;
  disabledTooltip?: string;
  isLoading: boolean;
  label: string;
  onClick: () => void;
};

function TelehealthActionButton({
  className,
  buttonClassName,
  disabled,
  disabledTooltip,
  isLoading,
  label,
  onClick,
}: TelehealthActionButtonProps) {
  return (disabled && !isNil(disabledTooltip)) ? (
    <ToolTip title={disabledTooltip}>
      <span className="inline-flex cursor-not-allowed">
        <button
          type="button"
          className={clsx(buttonClassName, className)}
          disabled={disabled || isLoading}
          onClick={onClick}
        >
          {isLoading ? (
            <Spinner size={16} />
          ) : (
            <>
              <VideocamOutlinedIcon className="!h-4 !w-4 !text-inherit" />
              {label}
            </>
          )}
        </button>
      </span>
    </ToolTip>
  ) : (
    <button
      type="button"
      className={clsx(buttonClassName, className)}
      disabled={disabled || isLoading}
      onClick={onClick}
    >
      {isLoading ? (
        <Spinner size={16} />
      ) : (
        <>
          <VideocamOutlinedIcon className="!h-4 !w-4 !text-inherit" />
          {label}
        </>
      )}
    </button>
  );
}

export default function JoinTelehealthVisitButton({
  encounter,
  className,
}: Props) {
  const notify = useNotify();
  const userInfo = useUserInfo();
  const currentUserId = userInfo?.user?.userId;
  const isTelehealthEnabledForUser = userInfo?.flags?.isTelehealthEnabled ?? false;
  const {
    encounterId = 0,
    userId: appointmentProviderUserId,
    isTelehealth: isTelehealthAppointment = false,
    telehealthProviderJoinInfo,
  } = encounter;

  const meetingExists = hasTelehealthMeeting(telehealthProviderJoinInfo);
  const doesAppointmentBelongToCurrentUser = currentUserId === (appointmentProviderUserId ?? 0);
  const shouldFetchJoinInfo = isTelehealthAppointment
    && doesAppointmentBelongToCurrentUser
    && isTelehealthEnabledForUser
    && meetingExists;

  const {
    data,
    isFetching,
    isError,
  } = useGetTelehealthJoinInfo(encounterId, shouldFetchJoinInfo);

  const {
    mutateAsync: fetchJoinInfo,
    isPending: isFetchingJoinInfo,
  } = useFetchTelehealthJoinInfo();

  const {
    mutateAsync: retryMeetingCreation,
    isPending: isRetryingMeeting,
  } = useRetryMeetingCreationForAppointment(encounterId);

  const canJoinMeeting = data?.canJoinMeeting ?? telehealthProviderJoinInfo?.canJoinMeeting ?? false;
  const joinUnavailableMessage = data?.joinUnavailableMessage
    ?? telehealthProviderJoinInfo?.joinUnavailableMessage
    ?? undefined;

  const onJoinVisit = useCallback(async () => {
    try {
      const providerJoinInfo = data ?? await fetchJoinInfo(encounterId);

      if (!providerJoinInfo.canJoinMeeting) {
        notify({
          variant: 'warning',
          message: providerJoinInfo.joinUnavailableMessage ?? telehealthMeetingUnavailableMessage,
        });
        return;
      }

      if (providerJoinInfo.startUrl) {
        window.open(providerJoinInfo.startUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      notify({
        variant: 'warning',
        message: telehealthMeetingUnavailableMessage,
      });
    } catch (error) {
      console.error(error);
    }
  }, [data, encounterId, fetchJoinInfo, notify]);

  const onRetryMeeting = useCallback(async () => {
    try {
      await retryMeetingCreation();
      notify({
        variant: 'success',
        message: 'Telehealth meeting created.',
      });
    } catch (error) {
      console.error(error);
    }
  }, [retryMeetingCreation, notify]);

  const disableJoinButton = !doesAppointmentBelongToCurrentUser || !meetingExists || !canJoinMeeting;
  const joinDisabledTooltip = getJoinDisabledTooltip(
    doesAppointmentBelongToCurrentUser,
    canJoinMeeting,
    joinUnavailableMessage,
  );
  const isJoinInfoLoading = isFetching || isFetchingJoinInfo || isRetryingMeeting;
  const showMeetingSetupButton = isTelehealthAppointment && (!meetingExists || isError);

  return (
    <>
      {isTelehealthAppointment && showMeetingSetupButton && (
        <TelehealthActionButton
          className={className}
          buttonClassName={retryButtonClassName}
          disabled={isJoinInfoLoading}
          isLoading={isJoinInfoLoading}
          label="Set up meeting"
          onClick={onRetryMeeting}
        />
      )}
      {isTelehealthAppointment && !showMeetingSetupButton && (
        <TelehealthActionButton
          className={className}
          buttonClassName={joinButtonClassName}
          disabled={disableJoinButton}
          disabledTooltip={joinDisabledTooltip}
          isLoading={isJoinInfoLoading}
          label="Join Telehealth Visit"
          onClick={onJoinVisit}
        />
      )}
    </>
  );
}
